import { formatValue } from './format'
import { categoryLabel, layoutRowLabels, placeBarValueLabel, spreadVertically } from './labels'
import { measureText, truncateToWidth } from './textMetrics'
import { color, FONT } from './tokens'
import type { HighlightVisualEvidence, HighlightVisualId } from './types'

// Semantic palette. Marks name a role; the role resolves to a FuseGrid token
// with a literal fallback so the exported HTML (which has no stylesheet) still
// paints. Teal/clay rather than green/red: the green-red pair is the single
// most common accessibility failure in financial charts, and the CFO chart kit
// has always avoided it.
const NEG = color('unfavorable')
const POS = color('favorable')
const WARN = color('attention')
const BRAND = color('brand')
const INK = color('ink')
const INK2 = color('ink-secondary')
const MUTED = color('muted')
const AXIS = color('axis')
const GRID = color('grid')
const SURFACE = color('surface')
const TRACK = color('band-neutral')

const clean = (value: unknown): string => String(value ?? '').trim()
const compareText = (left: string, right: string): number => left < right ? -1 : left > right ? 1 : 0

/**
 * Colour for a movement, polarity-corrected.
 *
 * `favourable` already accounts for whether the row is revenue or expense, so
 * an expense falling is teal. Colouring from `Math.sign(value)` — which is what
 * the first implementation did — paints every cost reduction as a crisis.
 */
const directionColor = (point: { favourable?: boolean | null; status?: string; value: number }): string => {
  if (point.favourable === true) return POS
  if (point.favourable === false) return NEG
  if (point.status === 'good') return POS
  if (point.status === 'bad') return NEG
  if (point.status === 'watch') return WARN
  return color('anchor')
}

const esc = (value: unknown) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character] || character))
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
// Categorical ramp. Entries must be distinct: an earlier version repeated the
// same hue at indices 0 and 1, so a three-band chart drew a three-entry legend
// over two visually identical bands.
const palette = [BRAND, POS, WARN, NEG, color('anchor'), INK2]

/**
 * Aging bands carry their own meaning, so they get a semantic ramp rather than
 * an arbitrary categorical one: fresh reads favourable, the tail reads
 * unfavourable, and the order is legible even in greyscale because the ramp
 * darkens monotonically.
 */
const agingColor = (group: string, index: number, total: number): string => {
  const key = String(group || '').toLowerCase()
  if (/current|^0|not.?due/.test(key)) return POS
  if (/90\+|over ?90|risk|120/.test(key)) return NEG
  if (/30|60|90|under/.test(key)) return WARN
  // Unlabelled bands fall back to position in the stack.
  if (index === 0) return POS
  if (index >= total - 1) return NEG
  return WARN
}
const values = (evidence: HighlightVisualEvidence) => evidence.points.map((item) => Number.isFinite(item.value) ? item.value : 0)
const maxAbs = (evidence: HighlightVisualEvidence) => Math.max(1, ...values(evidence).map(Math.abs))
/** Scene coordinate space. Every renderer draws into this box. */
export const SCENE_WIDTH = 420
export const SCENE_HEIGHT = 205
const EDGE = 4

/**
 * Room a label has before it runs off the canvas, given where it starts and
 * which way it grows. Replaces a fixed 19-character truncation, which is a
 * character count standing in for a width — the two are not the same, and the
 * gap between them is how "Total Payroll Expe…" ended up sitting on top of its
 * own value label.
 */
const roomFor = (x: number, anchor: string): number => {
  if (anchor === 'end') return Math.max(0, x - EDGE)
  if (anchor === 'middle') return Math.max(0, 2 * Math.min(x - EDGE, SCENE_WIDTH - EDGE - x))
  return Math.max(0, SCENE_WIDTH - EDGE - x)
}

const text = (
  x: number,
  y: number,
  value: string,
  anchor = 'start',
  fill = INK,
  size = 10,
  weight = 500,
  maxWidth?: number,
) => {
  const limit = maxWidth ?? roomFor(x, anchor)
  const fitted = truncateToWidth(String(value ?? ''), limit, { size, weight })
  if (!fitted) return ''
  // Tabular figures keep numeric columns aligned across rows; Inter's
  // proportional "1" is 35% narrower than its "0", which visibly ragged the
  // value column.
  const numeric = /\d/.test(fitted) ? ' style="font-variant-numeric:tabular-nums"' : ''
  return `<text x="${x}" y="${y}" text-anchor="${anchor}" fill="${fill}" font-size="${size}" font-weight="${weight}" font-family="${FONT.family}"${numeric}>${esc(fitted)}</text>`
}
const title = (value: string) => `<title>${esc(value)}</title>`

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/** Month labels shared by bridge endpoints and its explicit comparison key. */
const comparisonPeriodLabel = (period: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(clean(period))
  if (!match) return clean(period)
  const month = SHORT_MONTHS[Number(match[2]) - 1]
  return month ? `${month} ${match[1]}` : clean(period)
}

/**
 * Two-line category label for the compact waterfall columns.
 *
 * The old renderer truncated every label into a 52px single line, so the
 * governed names became "Total Cost of…" and two different endpoints both
 * read "Net Income ·…". Wrap at word boundaries and reserve line two for the
 * actual month on absolute endpoints.
 */
const bridgeLabel = (x: number, item: HighlightVisualEvidence['points'][number]): string => {
  const maxWidth = 52
  const size = 7
  const weight = 500
  const period = item.absolute ? comparisonPeriodLabel(item.period) : ''
  let lines: string[]
  if (period) {
    lines = [truncateToWidth(item.label, maxWidth, { size, weight }), truncateToWidth(period, maxWidth, { size, weight })]
  } else {
    const words = clean(item.label).split(/\s+/).filter(Boolean)
    let first = ''
    let second = ''
    for (const word of words) {
      const proposed = first ? `${first} ${word}` : word
      if (!second && measureText(proposed, { size, weight }) <= maxWidth) first = proposed
      else second = second ? `${second} ${word}` : word
    }
    lines = [first, truncateToWidth(second, maxWidth, { size, weight })].filter(Boolean)
  }
  // Keep a literal separator between tspans. It is visually collapsed by SVG,
  // but preserves "Operating Expenses" for copied text and assistive tech.
  return `<text x="${x}" y="186" text-anchor="middle" fill="${INK2}" font-size="${size}" font-weight="${weight}" font-family="${FONT.family}">${lines.map((line, index) => `<tspan x="${x}" dy="${index ? 10 : 0}">${esc(line)}</tspan>`).join(' ')}</text>`
}

/**
 * Margin leak map — a horizontal bridge from the prior result to the current
 * one, each driver a floating segment.
 *
 * Replaces a set of curved "loss ribbons" whose stroke-width was
 * `10 + index * 1.5` — the loudest channel on the chart encoded the row's
 * position in the list, which is to say nothing — and whose length was clamped
 * at a 34px floor, so every small driver looked identical to every other.
 *
 * A bridge rather than another ranked-bar chart, because the template's own
 * question is "where did the margin points go", and only a bridge shows the
 * running total surviving each step. It also keeps this template genuinely
 * distinct from `variance_terrain`; two templates that differ only in their
 * metadata strings would satisfy the diversity check while showing the reader
 * the same picture twice, which is what the first version did.
 */
function marginLeakMap(e: HighlightVisualEvidence): string {
  const points = e.points.slice(0, 6)
  if (!points.length) return ''
  const layout = layoutRowLabels(points.map((item) => item.label), {
    width: SCENE_WIDTH,
    paddingLeft: 6,
    paddingRight: 8,
    valueLabels: points.map((item) => item.displayValue),
  })

  // Running total after each step, starting from zero. The extent covers the
  // whole path so no segment is clipped.
  const running: number[] = []
  points.reduce((sum, item) => { running.push(sum + item.value); return sum + item.value }, 0)
  const lo = Math.min(0, ...running)
  const hi = Math.max(0, ...running)
  const span = Math.max(1, hi - lo)
  const toX = (value: number) => layout.plotX + ((value - lo) / span) * layout.plotWidth

  const top = 18
  const rowHeight = Math.min(28, (SCENE_HEIGHT - top - 18) / points.length)
  const barHeight = Math.min(13, rowHeight - 8)
  const zeroX = toX(0)

  const rows = points.map((item, index) => {
    const before = index ? running[index - 1] : 0
    const after = running[index]
    const centre = top + index * rowHeight + rowHeight / 2
    const x1 = toX(before)
    const x2 = toX(after)
    const barX = Math.min(x1, x2)
    const barW = Math.max(2, Math.abs(x2 - x1))
    const placement = placeBarValueLabel(item.displayValue, { baseX: x1, endX: x2, layout })
    // Connector from the previous step's running total down to this one, so the
    // eye can follow the total rather than re-adding the segments.
    const connector = index
      ? `<line x1="${x1.toFixed(2)}" y1="${(centre - rowHeight).toFixed(2)}" x2="${x1.toFixed(2)}" y2="${(centre - barHeight / 2).toFixed(2)}" stroke="${GRID}" stroke-width="1" stroke-dasharray="2 2"/>`
      : ''
    return `<g>${title(`${item.label}: ${item.displayValue}`)}${connector}`
      + text(layout.categoryX, centre + 3.5, categoryLabel(item.label, layout), 'start', INK2, FONT.category.size, FONT.category.weight, layout.categoryWidth)
      + `<rect x="${barX.toFixed(2)}" y="${(centre - barHeight / 2).toFixed(2)}" width="${barW.toFixed(2)}" height="${barHeight.toFixed(2)}" rx="2" fill="${directionColor(item)}"/>`
      + (placement.visible ? text(placement.x, centre + 3.5, item.displayValue, placement.anchor, placement.inside ? SURFACE : INK, FONT.value.size, FONT.value.weight) : '')
      + '</g>'
  }).join('')

  const baseline = `<line x1="${zeroX.toFixed(2)}" y1="${top - 4}" x2="${zeroX.toFixed(2)}" y2="${(top + points.length * rowHeight + 4).toFixed(2)}" stroke="${AXIS}" stroke-width="1.2"/>`
  return `${baseline}${rows}`
}

/** Shared row-chart engine for every ranked-driver scene. */
function rankedDriverRows(e: HighlightVisualEvidence, options: { zeroBaseline: boolean }): string {
  const points = e.points.slice(0, 6)
  if (!points.length) return ''
  const layout = layoutRowLabels(points.map((item) => item.label), {
    width: SCENE_WIDTH,
    paddingLeft: 6,
    paddingRight: 8,
    valueLabels: points.map((item) => item.displayValue),
  })
  const max = Math.max(1, ...points.map((item) => Math.abs(item.value)))

  const top = 20
  const rowHeight = Math.min(30, (SCENE_HEIGHT - top - 16) / points.length)
  const barHeight = Math.min(14, rowHeight - 8)

  // A signed chart needs a zero line in the middle of the plot; an all-negative
  // ranked list reads better growing from the left.
  const signed = options.zeroBaseline && points.some((item) => item.value > 0) && points.some((item) => item.value < 0)
  const baseX = signed ? layout.plotX + layout.plotWidth / 2 : layout.plotX
  const scale = signed ? layout.plotWidth / 2 : layout.plotWidth

  const rows = points.map((item, index) => {
    const y = top + index * rowHeight
    const centre = y + rowHeight / 2
    const magnitude = (Math.abs(item.value) / max) * scale
    const endX = signed
      ? baseX + (item.value >= 0 ? magnitude : -magnitude)
      : baseX + magnitude
    const fill = directionColor(item)
    const label = categoryLabel(item.label, layout)
    const placement = placeBarValueLabel(item.displayValue, { baseX, endX, layout })
    const barX = Math.min(baseX, endX)
    const barW = Math.max(2, Math.abs(endX - baseX))
    const valueText = placement.visible
      ? text(
          placement.x,
          centre + 3.5,
          item.displayValue,
          placement.anchor,
          placement.inside ? SURFACE : INK,
          FONT.value.size,
          FONT.value.weight,
        )
      : ''
    return `<g>${title(`${item.label}: ${item.displayValue}`)}`
      + text(layout.categoryX, centre + 3.5, label, 'start', INK2, FONT.category.size, FONT.category.weight, layout.categoryWidth)
      + `<rect x="${barX.toFixed(2)}" y="${(centre - barHeight / 2).toFixed(2)}" width="${barW.toFixed(2)}" height="${barHeight.toFixed(2)}" rx="2" fill="${fill}"/>`
      + valueText
      + '</g>'
  }).join('')

  const axis = signed
    ? `<line x1="${baseX.toFixed(2)}" y1="${top - 4}" x2="${baseX.toFixed(2)}" y2="${(top + points.length * rowHeight + 2).toFixed(2)}" stroke="${AXIS}" stroke-width="1.2"/>`
    : ''
  return `${axis}${rows}`
}

function revenueStaircase(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 7)
  const cumulative: number[] = []
  pts.reduce((sum, item) => { cumulative.push(sum + item.value); return sum + item.value }, 0)
  const min = Math.min(0, ...cumulative); const max = Math.max(1, ...cumulative); const span = Math.max(1, max - min)
  return pts.map((item, index) => {
    const before = index ? cumulative[index - 1] : 0; const after = cumulative[index]
    const x = 18 + index * 56; const y1 = 170 - ((before - min) / span) * 125; const y2 = 170 - ((after - min) / span) * 125
    const top = Math.min(y1, y2); const height = Math.max(4, Math.abs(y2 - y1))
    return `<line x1="${x - 5}" y1="${y1}" x2="${x + 48}" y2="${y1}" stroke="${AXIS}" stroke-dasharray="3 3"/><rect x="${x}" y="${top}" width="42" height="${height}" rx="4" fill="${item.value < 0 ? NEG : POS}"/>${text(x + 21, 192, item.label, 'middle', INK2, 8)}${text(x + 21, Math.max(11, top - 4), item.displayValue, 'middle', INK, 8, 700)}`
  }).join('')
}

function serviceMosaic(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 7); const total = Math.max(1, pts.reduce((sum, item) => sum + Math.abs(item.value), 0)); let x = 8
  return pts.map((item, index) => {
    const width = Math.max(38, Math.abs(item.value) / total * 390); const margin = item.secondaryValue == null ? 0.5 : clamp((item.secondaryValue + 1) / 2, 0.12, 1)
    const height = 62 + margin * 98; const y = 176 - height
    const result = `<rect x="${x}" y="${y}" width="${Math.max(31, width - 5)}" height="${height}" rx="7" fill="${palette[index % palette.length]}" fill-opacity="${0.45 + margin * 0.45}"/>${text(x + width / 2, y + 22, item.label, 'middle', SURFACE, 9, 750)}${text(x + width / 2, y + 38, item.displayValue, 'middle', SURFACE, 8, 650)}`
    x += width; return result
  }).join('')
}

function planRail(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 5); const max = maxAbs(e)
  return `<line x1="30" y1="95" x2="390" y2="95" stroke="${AXIS}" stroke-width="9" stroke-linecap="round"/>${pts.map((item, index) => { const x = 40 + index * (340 / Math.max(1, pts.length - 1)); const y = 95 - (item.value / max) * 48; return `<line x1="${x}" y1="95" x2="${x}" y2="${y}" stroke="${palette[index % palette.length]}" stroke-width="3"/><circle cx="${x}" cy="${y}" r="13" fill="${palette[index % palette.length]}"/>${text(x, y - 19, item.displayValue, 'middle', INK, 8, 700)}${text(x, 125, item.label, 'middle', INK2, 8)}` }).join('')}`
}

/**
 * Signed variance rows against a zero baseline.
 *
 * The previous version fixed the category label at x=5 and the zero line at
 * x=215, then placed the value label at a data-dependent x. With a 107px
 * account name and a bar reaching left to x=80, the two overlapped every time —
 * which is exactly what shipped. Both are now placed from measured widths.
 */
function varianceTerrain(e: HighlightVisualEvidence): string {
  return rankedDriverRows(e, { zeroBaseline: true })
}

function locationMatrix(e: HighlightVisualEvidence): string {
  const max = maxAbs(e); const groups = [...new Set(e.points.map((item) => item.group || item.label))].slice(0, 4); const periods = [...new Set(e.points.map((item) => item.period || 'Current'))].slice(0, 6)
  return `${periods.map((period, col) => text(132 + col * 48, 13, period.slice(-5), 'middle', MUTED, 8)).join('')}${groups.map((group, row) => `${text(5, 42 + row * 40, group)}${periods.map((period, col) => { const item = e.points.find((candidate) => (candidate.group || candidate.label) === group && (candidate.period || 'Current') === period); const value = item?.value || 0; return `<rect x="108" y="${22 + row * 40}" width="${periods.length * 48}" height="31" rx="6" fill="${GRID}"/><rect x="${112 + col * 48}" y="${25 + row * 40}" width="40" height="25" rx="4" fill="${value < 0 ? NEG : POS}" fill-opacity="${.18 + .78 * Math.abs(value) / max}"/>${item ? title(`${item.label}: ${item.displayValue}`) : ''}` }).join('')}`).join('')}`
}

const shortWeekLabel = (period: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(clean(period))
  if (!match) return clean(period)
  const month = SHORT_MONTHS[Number(match[2]) - 1]
  return month ? `${month} ${Number(match[3])}` : clean(period)
}

const cashEventLabel = (y: number, value: string): string => {
  const maxWidth = 112
  const size = 8.5
  const weight = 700
  const words = clean(value).split(/\s+/).filter(Boolean)
  let first = ''
  let second = ''
  for (const word of words) {
    const proposed = first ? `${first} ${word}` : word
    if (!second && measureText(proposed, { size, weight }) <= maxWidth) first = proposed
    else second = second ? `${second} ${word}` : word
  }
  if (!second) return text(6, y - 2, first, 'start', INK, size, weight, maxWidth)
  const lines = [first, truncateToWidth(second, maxWidth, { size, weight })]
  return `<text x="6" y="${y - 6}" text-anchor="start" fill="${INK}" font-size="${size}" font-weight="${weight}" font-family="${FONT.family}">`
    + lines.map((line, index) => `<tspan x="6" dy="${index ? 9 : 0}">${esc(line)}</tspan>`).join(' ')
    + '</text>'
}

/**
 * Named cash events placed on their scheduled week.
 *
 * The old scene drew one tall bar per event and pinned a dot above each bar.
 * That used both height and position for the same dollar value, truncated every
 * counterparty, and did not communicate timing at all. This is a real event
 * timeline: x is the scheduled week, marker area is amount, colour is cash
 * direction, and the source type remains directly labelled. Several markers
 * lining up on one guide is the collision the story is warning about.
 */
function cashCalendar(e: HighlightVisualEvidence): string {
  const points = e.points
    .slice(0, 6)
    .sort((a, b) => compareText(clean(a.period), clean(b.period)) || Math.abs(b.value) - Math.abs(a.value) || compareText(a.id, b.id))
  if (!points.length) return ''

  const periods = [...new Set(points.map((item) => clean(item.period)).filter(Boolean))].sort(compareText)
  const plotLeft = 132
  const plotRight = 350
  const top = 39
  const bottom = 176
  const max = Math.max(1, ...points.map((item) => Math.abs(item.value)))
  const xForPeriod = (period: string | null | undefined): number => {
    if (periods.length <= 1) return (plotLeft + plotRight) / 2
    const index = Math.max(0, periods.indexOf(clean(period)))
    return plotLeft + index * ((plotRight - plotLeft) / (periods.length - 1))
  }
  const yForIndex = (index: number): number => points.length <= 1
    ? (top + bottom) / 2
    : top + index * ((bottom - top) / (points.length - 1))

  const guides = periods.map((period) => {
    const x = xForPeriod(period)
    return `${text(x, 17, shortWeekLabel(period), 'middle', MUTED, 7, 650, 48)}`
      + `<line x1="${x.toFixed(2)}" y1="24" x2="${x.toFixed(2)}" y2="184" stroke="${GRID}" stroke-width="1" stroke-dasharray="3 3"/>`
  }).join('')

  const rows = points.map((item, index) => {
    const y = yForIndex(index)
    const x = xForPeriod(item.period)
    const radius = 4.5 + Math.sqrt(Math.abs(item.value) / max) * 5
    const provenance = clean(item.group).replace(/_/g, ' ') || 'served plan'
    const fill = directionColor(item)
    const marker = /document/i.test(provenance)
      ? `<rect x="${(x - radius).toFixed(2)}" y="${(y - radius).toFixed(2)}" width="${(radius * 2).toFixed(2)}" height="${(radius * 2).toFixed(2)}" rx="2" fill="${fill}"/>`
      : /model/i.test(provenance)
        ? `<path d="M ${x.toFixed(2)} ${(y - radius).toFixed(2)} L ${(x + radius).toFixed(2)} ${y.toFixed(2)} L ${x.toFixed(2)} ${(y + radius).toFixed(2)} L ${(x - radius).toFixed(2)} ${y.toFixed(2)} Z" fill="${fill}" fill-opacity=".48" stroke="${fill}" stroke-width="1.5"/>`
        : `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${radius.toFixed(2)}" fill="${fill}"/>`
    return `<g>${title(`${item.label} · ${shortWeekLabel(item.period)} · ${item.displayValue} · ${provenance}`)}`
      + `<line x1="${plotLeft}" y1="${y.toFixed(2)}" x2="${plotRight}" y2="${y.toFixed(2)}" stroke="${TRACK}" stroke-width="1.5"/>`
      + cashEventLabel(y, item.label)
      + text(6, y + 11, provenance, 'start', MUTED, 6.5, 550, 112)
      + marker
      + text(SCENE_WIDTH - 5, y + 3, item.displayValue, 'end', fill, 8.5, 750, 54)
      + '</g>'
  }).join('')

  const key = text(plotLeft, 201, 'Week scheduled', 'start', MUTED, 7, 600)
    + text(SCENE_WIDTH - 5, 201, 'Marker size = cash amount', 'end', MUTED, 7, 600)
  return `${guides}${rows}${key}`
}

function runwayField(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 40)
  return pts.map((item, index) => { const x = 24 + (index % 10) * 40; const y = 30 + Math.floor(index / 10) * 42; const bad = item.status === 'bad' || item.value < 0; return `<circle cx="${x}" cy="${y}" r="12" fill="${bad ? NEG : item.status === 'good' ? POS : WARN}"/><text x="${x}" y="${y + 3}" text-anchor="middle" fill="${SURFACE}" font-size="7" font-weight="800">${index + 1}</text>${title(`${item.label}: ${item.displayValue}`)}` }).join('') + `${text(8, 198, `${pts.filter((item) => item.status === 'bad' || item.value < 0).length} scenarios breach minimum cash`, 'start', INK, 10, 700)}`
}

function workingCapitalFlow(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 5)
  // Canonical cash-flow evidence has absolute opening/closing levels and
  // intervening cash effects. That is a real reconciled bridge, so use the
  // bridge grammar that understands levels versus movements. Older provider
  // payloads carry only signed account movements and use the compact view
  // below.
  if (pts.some((item) => item.absolute)) return legacyWaterfall(e)
  const max = maxAbs(e)
  const zeroX = 242
  const barRoom = 142
  const rowTop = 48
  const rowGap = 27
  const period = comparisonPeriodLabel(e.periods.at(-1) || e.dataThrough)
  const isCashEffect = e.sourceLineage.includes('cash_flow')

  // This is a MOVEMENT view, not a process diagram. The old renderer drew each
  // account as a giant rounded block joined by arrows, implying a
  // causal sequence that the source never asserted. It also trapped the full
  // account name inside 58px, which produced the screenshot's four identical
  // ellipses. A common zero line preserves the only relationship supported by
  // this evidence — signed magnitude — while direct labels keep every account
  // readable at board scale.
  const header = text(6, 14, e.title || 'Working-capital movements', 'start', INK, 9, 700, 285)
    + (period ? text(SCENE_WIDTH - 5, 14, period, 'end', MUTED, 7, 600, 92) : '')
    + text(zeroX - 6, 31, isCashEffect ? 'uses cash' : 'decrease', 'end', MUTED, 6.5, 600, 62)
    + text(zeroX + 6, 31, isCashEffect ? 'releases cash' : 'increase', 'start', MUTED, 6.5, 600, 62)
    + `<line x1="${zeroX}" y1="35" x2="${zeroX}" y2="${rowTop + Math.max(0, pts.length - 1) * rowGap + 13}" stroke="${AXIS}" stroke-width="1"/>`

  const rows = pts.map((item, index) => {
    const y = rowTop + index * rowGap
    const width = Math.max(2, Math.abs(item.value) / max * barRoom)
    const x = item.value < 0 ? zeroX - width : zeroX
    const fill = item.value < 0 ? NEG : directionColor(item)
    return `<g>${title(`${item.label} · ${item.displayValue}${item.period ? ` · ${comparisonPeriodLabel(item.period)}` : ''}`)}`
      + `<line x1="${zeroX - barRoom}" y1="${y + 5}" x2="${zeroX + barRoom}" y2="${y + 5}" stroke="${TRACK}" stroke-width="1"/>`
      + text(6, y + 8, item.label, 'start', INK2, 7.5, 600, 136)
      + `<rect x="${x.toFixed(2)}" y="${y}" width="${width.toFixed(2)}" height="10" rx="5" fill="${fill}" fill-opacity=".82"/>`
      + text(SCENE_WIDTH - 5, y + 8, item.displayValue, 'end', fill, 8, 750, 72)
      + '</g>'
  }).join('')

  return header + rows
}

function debtorConstellation(e: HighlightVisualEvidence): string {
  const maxX = maxAbs(e); const maxY = Math.max(1, ...e.points.map((item) => Math.abs(item.secondaryValue || 0)))
  return e.points.slice(0, 10).map((item, index) => { const x = 40 + Math.abs(item.value) / maxX * 330; const y = 172 - Math.abs(item.secondaryValue || 0) / maxY * 140; const radius = 6 + Math.sqrt(Math.abs(item.value) / maxX) * 14; return `<circle cx="${x}" cy="${y}" r="${radius}" fill="${palette[index % palette.length]}" fill-opacity=".76"/>${text(x, Math.max(11, y - radius - 4), item.label, 'middle', INK, 8, 650)}${index < 5 ? text(x, Math.min(194, y + radius + 12), item.displayValue, 'middle', MUTED, 7) : ''}` }).join('') + `<line x1="32" y1="180" x2="400" y2="180" stroke="${AXIS}"/><line x1="32" y1="12" x2="32" y2="180" stroke="${AXIS}"/>`
}

function collectionsCurves(e: HighlightVisualEvidence): string {
  const groups = [...new Set(e.points.map((item) => item.group || item.label))].slice(0, 4); const max = maxAbs(e)
  return groups.map((group, groupIndex) => { const pts = e.points.filter((item) => (item.group || item.label) === group).sort((a, b) => compareText(String(a.period), String(b.period))); const coords = pts.map((item, index) => `${32 + index * (350 / Math.max(1, pts.length - 1))},${172 - Math.abs(item.value) / max * 142}`).join(' '); const color = palette[groupIndex % palette.length]; return `<polyline points="${coords}" fill="none" stroke="${color}" stroke-width="3"/>${pts.map((item, index) => `<circle cx="${32 + index * (350 / Math.max(1, pts.length - 1))}" cy="${172 - Math.abs(item.value) / max * 142}" r="3" fill="${color}"/>`).join('')}${text(12 + groupIndex * 102, 14, group, 'start', color, 8, 700)}` }).join('')
}

function agingRibbon(e: HighlightVisualEvidence): string {
  const periods = [...new Set(e.points.map((item) => item.period || ''))].filter(Boolean); const groups = [...new Set(e.points.map((item) => item.group || item.label))]
  const totals = periods.map((period) => e.points.filter((item) => item.period === period).reduce((sum, item) => sum + Math.abs(item.value), 0)); const max = Math.max(1, ...totals)
  let lower = periods.map(() => 174)
  return groups.map((group, groupIndex) => { const upper = periods.map((period, index) => lower[index] - (Math.abs(e.points.find((item) => item.period === period && (item.group || item.label) === group)?.value || 0) / max) * 142); const polygon = [...upper.map((y, index) => `${28 + index * (360 / Math.max(1, periods.length - 1))},${y}`), ...lower.map((y, index) => `${28 + (lower.length - 1 - index) * (360 / Math.max(1, periods.length - 1))},${lower[lower.length - 1 - index]}`)].join(' '); lower = upper; const tone = agingColor(group, groupIndex, groups.length); return `<polygon points="${polygon}" fill="${tone}" fill-opacity=".78"/>${text(8 + groupIndex * 105, 14, group, 'start', tone, 8, 700, 100)}` }).join('') + periods.map((period, index) => text(28 + index * (360 / Math.max(1, periods.length - 1)), 196, period.slice(-5), 'middle', MUTED, 8)).join('')
}

function concentrationSkyline(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 8); const total = Math.max(1, pts.reduce((sum, item) => sum + Math.abs(item.value), 0)); const max = maxAbs(e); let running = 0
  const bars = pts.map((item, index) => { const x = 20 + index * 48; const h = Math.abs(item.value) / max * 125; running += Math.abs(item.value); const share = running / total; return `<rect x="${x}" y="${170 - h}" width="31" height="${h}" rx="4" fill="${palette[index % palette.length]}"/>${text(x + 15, 191, item.label, 'middle', INK2, 7)}<circle cx="${x + 15}" cy="${168 - share * 130}" r="3" fill="${INK}"/>` }).join('')
  running = 0; const line = pts.map((item, index) => { running += Math.abs(item.value); return `${35 + index * 48},${168 - running / total * 130}` }).join(' ')
  return bars + `<polyline points="${line}" fill="none" stroke="${INK}" stroke-width="2" stroke-dasharray="5 3"/>${text(400, 33, '100% cumulative', 'end', INK, 8, 700)}`
}

function expenseFingerprint(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 24); const sorted = [...values(e)].sort((a, b) => a - b); const q1 = sorted[Math.floor((sorted.length - 1) * .25)] || 0; const q3 = sorted[Math.floor((sorted.length - 1) * .75)] || 0; const iqr = Math.max(1, q3 - q1); const low = q1 - 1.5 * iqr; const high = q3 + 1.5 * iqr; const min = Math.min(...sorted, low); const max = Math.max(...sorted, high); const span = Math.max(1, max - min); const x = (v: number) => 24 + ((v - min) / span) * 370
  return `<rect x="${x(low)}" y="72" width="${Math.max(2, x(high) - x(low))}" height="60" rx="10" fill="${POS}" fill-opacity=".18"/>${text((x(low) + x(high)) / 2, 66, 'normal operating band', 'middle', POS, 9, 700)}<line x1="24" y1="103" x2="394" y2="103" stroke="${AXIS}"/>${pts.map((item, index) => { const outlier = item.value < low || item.value > high; return `<circle cx="${x(item.value)}" cy="${91 + (index % 3) * 13}" r="${outlier ? 7 : 4}" fill="${outlier ? NEG : BRAND}"/>${outlier ? text(x(item.value), 151 + (index % 2) * 13, item.label, 'middle', INK, 7, 650) : ''}` }).join('')}`
}

function forecastFan(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 12); const max = maxAbs(e); const upper = pts.map((item, index) => `${25 + index * (365 / Math.max(1, pts.length - 1))},${102 - Math.abs(item.secondaryValue ?? item.value) / max * 76}`).join(' '); const lower = [...pts].reverse().map((item, reverseIndex) => { const index = pts.length - 1 - reverseIndex; return `${25 + index * (365 / Math.max(1, pts.length - 1))},${102 + Math.abs(item.secondaryValue ?? item.value) / max * 76}` }).join(' '); const center = pts.map((item, index) => `${25 + index * (365 / Math.max(1, pts.length - 1))},${102 - item.value / max * 42}`).join(' ')
  return `<polygon points="${upper} ${lower}" fill="${BRAND}" fill-opacity=".28"/><polyline points="${center}" fill="none" stroke="${BRAND}" stroke-width="3"/>${pts.map((item, index) => text(25 + index * (365 / Math.max(1, pts.length - 1)), 195, item.label, 'middle', MUTED, 7)).join('')}${text(390, 18, 'uncertainty widens by horizon', 'end', BRAND, 9, 700)}`
}

function legacySparkline(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(-12); const min = Math.min(...pts.map((item) => item.value)); const max = Math.max(...pts.map((item) => item.value)); const span = Math.max(1, max - min)
  const coords = pts.map((item, index) => `${26 + index * (366 / Math.max(1, pts.length - 1))},${168 - ((item.value - min) / span) * 126}`)
  const area = `26,178 ${coords.join(' ')} 392,178`
  const last = pts.at(-1); const prior = pts.at(-2); const improving = !prior || !last ? true : last.value >= prior.value
  return `<polygon points="${area}" fill="url(#signalArea)" opacity=".48"/><polyline points="${coords.join(' ')}" fill="none" stroke="${improving ? BRAND : NEG}" stroke-width="5" stroke-linejoin="round"/>${pts.map((item, index) => `<circle cx="${26 + index * (366 / Math.max(1, pts.length - 1))}" cy="${168 - ((item.value - min) / span) * 126}" r="${index === pts.length - 1 ? 7 : 3}" fill="${index === pts.length - 1 ? INK : BRAND}">${title(`${item.label}: ${item.displayValue}`)}</circle>`).join('')}${last ? text(392, 28, last.displayValue, 'end', INK, 16, 800) : ''}${text(26, 198, pts[0]?.label || '', 'start', MUTED, 8)}${text(392, 198, last?.label || '', 'end', MUTED, 8)}`
}

function legacyTrendLine(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(-12); const min = Math.min(...pts.map((item) => item.value)); const max = Math.max(...pts.map((item) => item.value)); const pad = Math.max(1, (max - min) * .15); const span = Math.max(1, max - min + pad * 2)
  const x = (index: number) => 34 + index * (346 / Math.max(1, pts.length - 1)); const y = (value: number) => 174 - ((value - min + pad) / span) * 142
  const coords = pts.map((item, index) => `${x(index)},${y(item.value)}`).join(' '); const last = pts.at(-1)
  return `<line x1="34" y1="174" x2="392" y2="174" stroke="${AXIS}"/><line x1="34" y1="24" x2="34" y2="174" stroke="${AXIS}"/><polyline points="${coords}" fill="none" stroke="${BRAND}" stroke-width="4" stroke-linejoin="round"/>${pts.map((item, index) => `<circle cx="${x(index)}" cy="${y(item.value)}" r="${index === pts.length - 1 ? 7 : 3}" fill="${index === pts.length - 1 ? WARN : BRAND}">${title(`${item.label}: ${item.displayValue}`)}</circle>`).join('')}${last ? `${text(390, Math.max(18, y(last.value) - 14), last.displayValue, 'end', INK, 12, 800)}${text(390, Math.min(198, y(last.value) + 20), last.label, 'end', MUTED, 8)}` : ''}`
}

function legacyWaterfall(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 7)
  if (!pts.length) return ''
  // A bridge mixes levels and deltas. `absolute` points ARE the running total;
  // the rest accumulate onto it. Treating an opening balance as a movement
  // would offset every bar after it by that balance — the single largest number
  // on the chart, wrong, and wrong invisibly.
  const totals: number[] = []
  let running = 0
  for (const item of pts) {
    running = item.absolute ? item.value : running + item.value
    totals.push(running)
  }
  const min = Math.min(0, ...totals)
  const max = Math.max(1, ...totals)
  const span = Math.max(1, max - min)
  // Reserve a clear top line for the comparison and two bottom lines for full
  // category names. The previous chart spent the whole canvas on bars and then
  // tried to squeeze both context and labels into the remaining pixels.
  const y = (value: number) => 166 - ((value - min) / span) * 124
  const periods = e.periods.slice(-2).map(comparisonPeriodLabel).filter(Boolean)
  const comparison = periods.length === 2
    ? text(210, 12, `${periods[0]} → ${periods[1]}`, 'middle', MUTED, 8, 600, 220)
    : ''
  const bars = pts.map((item, index) => {
    const after = totals[index]
    // An absolute bar is a column from the axis; a delta is a floating bar from
    // the prior running total.
    const before = item.absolute ? Math.max(min, 0) : (index ? totals[index - 1] : 0)
    const x = 24 + index * 54
    const top = Math.min(y(before), y(after))
    const height = Math.max(5, Math.abs(y(after) - y(before)))
    const fill = item.absolute ? AXIS : item.value < 0 ? NEG : POS
    const connector = index && !item.absolute
      ? `<line x1="${x - 18}" y1="${y(before)}" x2="${x}" y2="${y(before)}" stroke="${AXIS}" stroke-dasharray="3 3"/>`
      : ''
    return `${connector}<rect x="${x}" y="${top}" width="38" height="${height}" rx="5" fill="${fill}"/>`
      + text(x + 19, Math.max(23, top - 6), item.displayValue, 'middle', INK, 8, 750, 52)
      + bridgeLabel(x + 19, item)
  }).join('')
  return comparison + bars
}

function legacyLollipop(e: HighlightVisualEvidence): string {
  const points = e.points.slice(0, 6)
  if (!points.length) return ''
  const layout = layoutRowLabels(points.map((item) => item.label), {
    width: SCENE_WIDTH,
    paddingLeft: 6,
    paddingRight: 8,
    valueLabels: points.map((item) => item.displayValue),
  })
  const max = Math.max(1, ...points.map((item) => Math.abs(item.value)))
  const signed = points.some((item) => item.value > 0) && points.some((item) => item.value < 0)
  const baseX = signed ? layout.plotX + layout.plotWidth / 2 : layout.plotX
  const scale = (signed ? layout.plotWidth / 2 : layout.plotWidth) - 10
  const top = 22
  const rowHeight = Math.min(30, (SCENE_HEIGHT - top - 16) / points.length)

  const rows = points.map((item, index) => {
    const y = top + index * rowHeight + rowHeight / 2
    const magnitude = (Math.abs(item.value) / max) * scale
    const end = baseX + (signed && item.value < 0 ? -magnitude : magnitude)
    const stroke = directionColor(item)
    const placement = placeBarValueLabel(item.displayValue, { baseX: baseX + (end >= baseX ? 8 : -8), endX: end + (end >= baseX ? 8 : -8), layout })
    // The benchmark this deviation is measured against. Without it the reader
    // sees a bar of unknown provenance; with it the chart states its own
    // comparison, which is the entire point of a lollipop benchmark.
    const reference = item.secondaryValue == null
      ? ''
      : `<line x1="${baseX.toFixed(2)}" y1="${(y - 7).toFixed(2)}" x2="${baseX.toFixed(2)}" y2="${(y + 7).toFixed(2)}" stroke="${AXIS}" stroke-width="1.6" stroke-dasharray="2 2"/>`
    return `<g>${title(`${item.label}: ${item.displayValue}${item.secondaryDisplayValue ? ` vs ${item.secondaryDisplayValue} normal` : ''}`)}`
      + text(layout.categoryX, y + 3.5, categoryLabel(item.label, layout), 'start', INK2, FONT.category.size, FONT.category.weight, layout.categoryWidth)
      + reference
      + `<line x1="${baseX.toFixed(2)}" y1="${y.toFixed(2)}" x2="${end.toFixed(2)}" y2="${y.toFixed(2)}" stroke="${stroke}" stroke-width="3"/>`
      + `<circle cx="${end.toFixed(2)}" cy="${y.toFixed(2)}" r="5" fill="${stroke}"/>`
      + (placement.visible ? text(placement.x, y + 3.5, item.displayValue, placement.anchor, placement.inside ? SURFACE : INK, FONT.value.size, FONT.value.weight) : '')
      + '</g>'
  }).join('')

  return `<line x1="${baseX.toFixed(2)}" y1="${top - 6}" x2="${baseX.toFixed(2)}" y2="${(top + points.length * rowHeight + 2).toFixed(2)}" stroke="${AXIS}" stroke-width="1.2"/>${rows}`
}

function legacyProgressRing(e: HighlightVisualEvidence): string {
  const item = e.points[0]; const raw = Math.abs(item?.secondaryValue ?? item?.value ?? 0); const progress = clamp(raw > 1 ? raw / 100 : raw, 0, 1); const radius = 67; const circumference = 2 * Math.PI * radius; const color = item?.status === 'bad' ? NEG : item?.status === 'watch' ? WARN : BRAND
  return `<circle cx="130" cy="103" r="${radius}" fill="none" stroke="${GRID}" stroke-width="18"/><circle cx="130" cy="103" r="${radius}" fill="none" stroke="${color}" stroke-width="18" stroke-linecap="round" stroke-dasharray="${progress * circumference} ${circumference}" transform="rotate(-90 130 103)"/>${text(130, 98, item?.displayValue || '', 'middle', INK, 18, 850)}${text(130, 118, item?.label || 'Progress', 'middle', MUTED, 9, 650)}${text(235, 78, `${Math.round(progress * 100)}% of reference`, 'start', color, 13, 800)}${text(235, 103, e.title, 'start', INK, 10, 650)}${text(235, 128, progress >= .85 ? 'Decision threshold near' : 'Capacity remains', 'start', MUTED, 9)}`
}

function legacyAgingBars(e: HighlightVisualEvidence): string {
  const latest = e.periods.at(-1); const pts = (latest ? e.points.filter((item) => item.period === latest) : e.points).slice(0, 6); const max = Math.max(1, ...pts.map((item) => Math.abs(item.value)))
  return pts.map((item, index) => { const y = 25 + index * 34; const width = Math.max(8, Math.abs(item.value) / max * 250); const color = item.status === 'bad' || /90|risk/i.test(item.label) ? NEG : item.status === 'watch' ? WARN : POS; return `${text(8, y + 7, item.label)}<rect x="128" y="${y - 8}" width="260" height="22" rx="11" fill="${TRACK}"/><rect x="128" y="${y - 8}" width="${width}" height="22" rx="11" fill="${color}"/>${text(400, y + 7, item.displayValue, 'end', INK, 9, 750)}` }).join('')
}

function legacyArBridge(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 7); const max = maxAbs(e); let running = 0
  return pts.map((item, index) => { const start = running; running += item.value; const x = 20 + index * 55; const y1 = 108 - start / max * 62; const y2 = 108 - running / max * 62; const top = Math.min(y1, y2); const height = Math.max(5, Math.abs(y2 - y1)); const risk = item.status === 'bad' || /90|risk/i.test(item.label); return `<rect x="${x}" y="${top}" width="38" height="${height}" rx="5" fill="${risk ? NEG : item.value < 0 ? POS : WARN}"/>${text(x + 19, Math.max(12, top - 6), item.displayValue, 'middle', INK, 8, 700)}${text(x + 19, 190, item.label, 'middle', INK2, 7)}${index ? `<line x1="${x - 16}" y1="${y1}" x2="${x}" y2="${y1}" stroke="${AXIS}"/>` : ''}` }).join('') + `${text(12, 14, 'A/R migration bridge', 'start', BRAND, 10, 750)}`
}

function legacyForecastBand(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(-12); const max = Math.max(1, ...pts.flatMap((item) => [Math.abs(item.value), Math.abs(item.value + (item.secondaryValue || 0)), Math.abs(item.value - (item.secondaryValue || 0))])); const x = (index: number) => 28 + index * (360 / Math.max(1, pts.length - 1)); const y = (value: number) => 104 - value / max * 75
  const upper = pts.map((item, index) => `${x(index)},${y(item.value + Math.abs(item.secondaryValue || 0))}`).join(' '); const lower = [...pts].reverse().map((item, reverseIndex) => { const index = pts.length - 1 - reverseIndex; return `${x(index)},${y(item.value - Math.abs(item.secondaryValue || 0))}` }).join(' '); const center = pts.map((item, index) => `${x(index)},${y(item.value)}`).join(' ')
  return `<polygon points="${upper} ${lower}" fill="url(#forecastBand)" opacity=".65"/><polyline points="${center}" fill="none" stroke="${BRAND}" stroke-width="4"/>${pts.map((item, index) => `<circle cx="${x(index)}" cy="${y(item.value)}" r="3" fill="${INK}">${title(`${item.label}: ${item.displayValue}`)}</circle>`).join('')}${text(392, 18, 'uncertainty range', 'end', BRAND, 9, 750)}${text(28, 196, pts[0]?.label || '', 'start', MUTED, 8)}${text(392, 196, pts.at(-1)?.label || '', 'end', MUTED, 8)}`
}

function legacyScenarioLines(e: HighlightVisualEvidence): string {
  const groups = [...new Set(e.points.map((item) => item.group || item.label))].slice(0, 4); const max = maxAbs(e); const colors = [BRAND, POS, NEG, WARN]
  return groups.map((group, groupIndex) => { const pts = e.points.filter((item) => (item.group || item.label) === group).sort((a, b) => compareText(String(a.period), String(b.period))); const coords = pts.map((item, index) => `${34 + index * (345 / Math.max(1, pts.length - 1))},${104 - item.value / max * 72}`).join(' '); const last = pts.at(-1); const color = colors[groupIndex]; return `<polyline points="${coords}" fill="none" stroke="${color}" stroke-width="${groupIndex === 0 ? 4 : 3}" stroke-dasharray="${groupIndex === 0 ? '' : '7 4'}"/>${last ? `${text(392, 104 - last.value / max * 72 + 4 + groupIndex * 11, `${group} ${last.displayValue}`, 'end', color, 8, 750)}` : ''}` }).join('') + `<line x1="28" y1="104" x2="392" y2="104" stroke="${GRID}"/>`
}

function legacyFootballField(e: HighlightVisualEvidence): string {
  const pts = e.points.slice(0, 5); const lows = pts.map((item) => Math.min(item.value, item.secondaryValue ?? item.value)); const highs = pts.map((item) => Math.max(item.value, item.secondaryValue ?? item.value)); const min = Math.min(...lows); const max = Math.max(...highs); const span = Math.max(1, max - min); const x = (value: number) => 135 + (value - min) / span * 250
  return pts.map((item, index) => { const y = 28 + index * 36; const low = Math.min(item.value, item.secondaryValue ?? item.value); const high = Math.max(item.value, item.secondaryValue ?? item.value); const color = palette[index % palette.length]; return `${text(7, y + 6, item.label)}<line x1="${x(low)}" y1="${y}" x2="${x(high)}" y2="${y}" stroke="${color}" stroke-width="16" stroke-linecap="round"/>${text(x(low), y + 4, display(low, e.unit), 'middle', SURFACE, 7, 800)}${text(x(high), y + 4, display(high, e.unit), 'middle', SURFACE, 7, 800)}` }).join('')
}

function legacyHealthRange(e: HighlightVisualEvidence): string {
  const item = e.points[0]; const anchors = e.points.slice(1, 3).map((point) => point.value); const low = anchors[0] ?? Math.min(0, item?.value || 0); const high = anchors[1] ?? Math.max(1, item?.secondaryValue ?? item?.value ?? 1); const min = Math.min(low, item?.value || 0); const max = Math.max(high, item?.value || 0); const span = Math.max(1, max - min); const x = (value: number) => 32 + (value - min) / span * 356; const value = item?.value || 0; const color = item?.status === 'bad' || value < low || value > high ? NEG : item?.status === 'watch' ? WARN : POS
  return `<rect x="32" y="82" width="356" height="42" rx="21" fill="${TRACK}"/><rect x="${x(low)}" y="82" width="${Math.max(3, x(high) - x(low))}" height="42" rx="21" fill="${POS}" fill-opacity=".28"/><line x1="${x(value)}" y1="62" x2="${x(value)}" y2="144" stroke="${color}" stroke-width="6"/><circle cx="${x(value)}" cy="62" r="10" fill="${color}"/>${text(x(value), 44, item?.displayValue || '', 'middle', INK, 14, 850)}${text((x(low) + x(high)) / 2, 160, `healthy range ${display(low, e.unit)} to ${display(high, e.unit)}`, 'middle', POS, 8, 700)}${text(210, 193, item?.label || e.title, 'middle', INK2, 10, 650)}`
}

/**
 * Two governed series on independent axes.
 *
 * Reads the two `group`s the evidence builder emits, rather than a `value` plus
 * a `secondaryValue` on one point — the pair comes from a declared registry
 * (`metricPairs.ts`) and each side keeps its own unit, so they cannot be
 * conflated into a single scale.
 *
 * Both series are direct-labelled at their right edge with their own last value
 * and unit. A dual-axis chart without that is unreadable: the reader has no way
 * to tell which line belongs to which scale, and the second axis is precisely
 * where a two-axis chart misleads.
 */
function legacyDualAxis(e: HighlightVisualEvidence): string {
  const groups = [...new Set(e.points.map((item) => item.group || item.label))].filter(Boolean).slice(0, 2)
  if (groups.length < 2) return ''
  const periods = [...new Set(e.points.map((item) => clean(item.period)))].filter(Boolean).sort(compareText)
  if (periods.length < 2) return ''

  const left = 30
  const right = SCENE_WIDTH - 30
  const top = 30
  const bottom = 168
  const xFor = (index: number) => left + (index * (right - left)) / Math.max(1, periods.length - 1)

  const series = groups.map((group, index) => {
    const points = periods.map((period) =>
      e.points.find((item) => (item.group || item.label) === group && clean(item.period) === period) || null)
    const finite = points.filter(Boolean).map((item) => item!.value)
    // Independent scales, each padded and anchored at zero where the data allows
    // — a series that never touches its own axis floor reads as flatter than it is.
    const lo = Math.min(0, ...finite)
    const hi = Math.max(...finite, lo + 1)
    const span = Math.max(1, hi - lo)
    const yFor = (value: number) => bottom - ((value - lo) / span) * (bottom - top)
    const stroke = index === 0 ? BRAND : WARN
    const dash = index === 0 ? '' : ' stroke-dasharray="6 3"'
    const path = points
      .map((item, i) => (item ? `${xFor(i).toFixed(2)},${yFor(item.value).toFixed(2)}` : null))
      .filter(Boolean)
      .join(' ')
    const last = points.filter(Boolean).at(-1)!
    return { group, stroke, dash, path, last, y: yFor(last.value), label: last.label }
  })

  // Keep the two end labels from colliding when the series converge.
  const labelYs = spreadVertically(series.map((s, i) => ({ key: String(i), y: s.y })), { minGap: 13, top: top + 6, bottom: bottom - 2 })

  const lines = series.map((s) =>
    `<polyline points="${s.path}" fill="none" stroke="${s.stroke}" stroke-width="2.4" stroke-linejoin="round"${s.dash}/>`
    + `<circle cx="${right.toFixed(2)}" cy="${s.y.toFixed(2)}" r="3.5" fill="${s.stroke}"/>`).join('')

  const endLabels = series.map((s, i) =>
    text(right - 6, (labelYs.get(String(i)) ?? s.y) + 3.5, `${s.label} ${s.last.displayValue}`, 'end', s.stroke, FONT.annotation.size, FONT.annotation.weight)).join('')

  const axis = `<line x1="${left}" y1="${bottom}" x2="${right}" y2="${bottom}" stroke="${AXIS}" stroke-width="1.2"/>`
  const periodLabels = text(left, bottom + 18, periods[0], 'start', MUTED, FONT.axis.size, FONT.axis.weight)
    + text(right, bottom + 18, periods.at(-1) || '', 'end', MUTED, FONT.axis.size, FONT.axis.weight)
  // The title carries what divergence means; without it the reader is left to
  // invent a relationship from two lines on unrelated scales.
  const subtitle = text(left, 18, e.title, 'start', INK2, FONT.annotation.size, FONT.annotation.weight, right - left)

  return `${subtitle}${axis}${lines}${endLabels}${periodLabels}`
}

// Second formatter deleted. This file used to carry its own, which disagreed
// with evidence.ts on sign placement and precision, so the same figure could be
// written two ways on one card.
const display = (value: number, unit: string): string => formatValue(value, unit)

export function buildHighlightSvg(templateId: HighlightVisualId, evidence: HighlightVisualEvidence): string {
  const renderers: Record<HighlightVisualId, (item: HighlightVisualEvidence) => string> = {
    margin_leak_map: marginLeakMap,
    revenue_movement_staircase: revenueStaircase,
    service_line_profit_mosaic: serviceMosaic,
    plan_decision_rail: planRail,
    variance_terrain: varianceTerrain,
    location_pressure_matrix: locationMatrix,
    cash_collision_calendar: cashCalendar,
    runway_outcome_field: runwayField,
    working_capital_current_map: workingCapitalFlow,
    debtor_constellation: debtorConstellation,
    collections_velocity_curves: collectionsCurves,
    aging_migration_ribbon: agingRibbon,
    concentration_skyline: concentrationSkyline,
    expense_fingerprint: expenseFingerprint,
    forecast_accuracy_fan: forecastFan,
    legacy_kpi_sparkline: legacySparkline,
    legacy_trend_line: legacyTrendLine,
    legacy_waterfall_bridge: legacyWaterfall,
    legacy_lollipop_benchmark: legacyLollipop,
    legacy_progress_ring: legacyProgressRing,
    legacy_aging_bars: legacyAgingBars,
    legacy_ar_aging_bridge: legacyArBridge,
    legacy_forecast_band: legacyForecastBand,
    legacy_scenario_lines: legacyScenarioLines,
    legacy_valuation_football_field: legacyFootballField,
    legacy_health_range_bar: legacyHealthRange,
    legacy_dual_axis_trend: legacyDualAxis,
  }
  const summary = `${evidence.title}. ${evidence.points.slice(0, 5).map((item) => `${item.label}: ${item.displayValue}`).join('; ')}`
  // Ids must be unique per document: three highlight cards inline three SVGs on
  // one page, and repeated ids make every `url(#…)` reference resolve to the
  // first card's definition. Scoping by the evidence hash keeps them distinct
  // and still deterministic — no counter, no clock, so the same evidence always
  // produces byte-identical markup and the content hash stays meaningful.
  const scope = evidence.hash.replace(/[^a-z0-9]/gi, '')
  const body = renderers[templateId](evidence)
    .replace(/url\(#([a-zA-Z][\w-]*)\)/g, (_match, id: string) => `url(#${id}-${scope})`)
  // The movement view is deliberately row-based and shorter than the shared
  // chart scene. Keeping the universal 205-unit canvas left a blank quarter of
  // the SVG beneath four rows, which became a large empty slab when the single
  // cockpit card scaled to its board width.
  const sceneHeight = templateId === 'working_capital_current_map' && !evidence.points.some((item) => item.absolute)
    ? Math.max(150, Math.min(SCENE_HEIGHT, 46 + Math.min(5, evidence.points.length) * 27))
    : SCENE_HEIGHT
  // No filters. `feGaussianBlur` is one of the SVG features that rasterizes or
  // silently drops in PDF renderers, and a glow carries no information anyway.
  return `<svg viewBox="0 0 ${SCENE_WIDTH} ${sceneHeight}" role="img" aria-label="${esc(summary)}" xmlns="http://www.w3.org/2000/svg">`
    + `<defs>`
    + `<marker id="arrow-${scope}" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><path d="M0 0 L7 3.5 L0 7 z" fill="${MUTED}"/></marker>`
    + `<linearGradient id="signalArea-${scope}" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${BRAND}" stop-opacity=".28"/><stop offset="1" stop-color="${BRAND}" stop-opacity="0"/></linearGradient>`
    + `<linearGradient id="forecastBand-${scope}" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${BRAND}" stop-opacity=".10"/><stop offset="1" stop-color="${BRAND}" stop-opacity=".30"/></linearGradient>`
    + `</defs>${body}</svg>`
}
